import { create } from 'zustand';
import type { BulkOperation } from '@/lib/email-accounts';

/**
 * The active async bulk job's descriptor. Held in a module-level Zustand store
 * (not URL / component state) so the docked `BulkJobProgressPanel` survives the
 * table remounting within the page — e.g. a filter change that swaps the list.
 *
 * Intentionally NOT persisted: a full page reload loses the panel, matching the
 * Redis-only ephemeral precedent (the job keeps running server-side; there is
 * no cancel endpoint). Dismissing the panel clears this — it hides the tray,
 * it does not stop the job.
 */
export type ActiveBulkJob = {
  jobId: string;
  operation: BulkOperation;
  total: number;
  /** Live status mirrored from BulkJobProgressPanel's Firestore subscription
   *  (undefined until the first snapshot arrives). */
  status?: 'pending' | 'running' | 'complete' | 'failed';
};

export interface BulkJobState {
  activeJob: ActiveBulkJob | null;
  setActiveJob: (job: ActiveBulkJob) => void;
  /** Patches status onto the currently-active job, guarded by jobId so a
   *  late/stale update can't clobber a newer job. No-op if jobId doesn't match. */
  setJobStatus: (jobId: string, status: ActiveBulkJob['status']) => void;
  clearActiveJob: () => void;
}

export const useBulkJobStore = create<BulkJobState>()((set) => ({
  activeJob: null,
  setActiveJob: (activeJob) => set({ activeJob }),
  setJobStatus: (jobId, status) =>
    set((s) =>
      s.activeJob?.jobId === jobId ? { activeJob: { ...s.activeJob, status } } : {},
    ),
  clearActiveJob: () => set({ activeJob: null }),
}));

/** True while a bulk-DELETE job is active and not yet terminal — the single
 *  gate both the list-query poll and the footer's silent-fetch suppression
 *  read, so they can never disagree. `status === undefined` (job just
 *  enqueued, no Firestore snapshot yet) correctly evaluates to "still
 *  polling" — polling starts the instant a delete job is set. */
export function useIsBulkDeletePolling(): boolean {
  return useBulkJobStore(
    (s) =>
      s.activeJob?.operation === 'delete' &&
      s.activeJob.status !== 'complete' &&
      s.activeJob.status !== 'failed',
  );
}
